import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Button } from "../../../components/ui/button";

export interface G360NavTab<K extends string = string> {
  key: K;
  label: string;
  /** Optional — the CM asset-score bands use a count badge instead. */
  icon?: LucideIcon;
  /** Trailing adornment, e.g. g360's SegmentedControl `hint` count badge. */
  hint?: ReactNode;
  disabled?: boolean;
}

interface G360NavTabsProps<K extends string> {
  tabs: G360NavTab<K>[];
  activeTab: K;
  onTabChange: (key: K) => void;
  ariaLabel: string;
  /** `"sm"` | `"md"` | `"lg"` tab sizing */
  size?: "sm" | "md" | "lg";
  variant?: "solid" | "subtle";
  className?: string;
}

/** Segmented tab bar for IT Compliance, CISO Insights, and User Access Review.
 * Active item uses a solid primary fill with high-contrast text and icon. */
export function G360NavTabs<K extends string>({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel,
  size = "md",
  variant = "solid",
  className = "",
}: G360NavTabsProps<K>) {
  const isSolid = variant === "solid";

  const sizeClasses = {
    sm: {
      nav: "h-9 min-h-9 text-xs p-1 gap-1 rounded-lg",
      btn: "h-7 min-h-7 gap-1.5 px-3 text-xs rounded-md",
      icon: "size-3.5",
    },
    md: {
      nav: "h-11 min-h-11 text-sm p-1 gap-1.5 rounded-xl",
      btn: "h-9 min-h-9 gap-2 px-4 text-sm rounded-lg",
      icon: "size-4",
    },
    lg: {
      nav: "h-12 min-h-12 text-sm p-1.5 gap-2 rounded-xl",
      btn: "h-9 min-h-9 gap-2.5 px-6 text-sm font-semibold rounded-lg",
      icon: "size-5",
    },
  }[size] ?? {
    nav: "h-11 min-h-11 text-sm p-1 gap-1.5 rounded-xl",
    btn: "h-9 min-h-9 gap-2 px-4 text-sm rounded-lg",
    icon: "size-4",
  };

  return (
    <nav
      aria-label={ariaLabel}
      className={`tabs-scroll inline-flex shrink-0 w-fit items-center overflow-x-auto border border-border/60 bg-card/80 shadow-xs ${sizeClasses.nav} ${className}`}
    >
      {tabs.map(({ key, label, icon: Icon, hint, disabled }) => {
        const isActive = activeTab === key;
        return (
          <Button
            key={key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={disabled}
            variant="ghost"
            size="none"
            onClick={() => onTabChange(key)}
            className={`group relative inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap font-medium transition-all duration-150 ease-out select-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${sizeClasses.btn} ${
              isActive
                ? isSolid
                  ? "bg-gradient-to-r from-[#2563eb] via-[#4f46e5] to-[#6366f1] text-white font-semibold shadow-md overflow-hidden"
                  : "bg-primary/10 font-semibold text-primary"
                : "bg-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            }`}
          >
            {Icon ? (
              <Icon
                className={`shrink-0 transition-colors ${sizeClasses.icon} ${
                  isActive
                    ? isSolid
                      ? "text-white"
                      : "text-primary"
                    : "text-muted-foreground group-hover:text-foreground"
                }`}
              />
            ) : null}
            {label}
            {hint}
            {isActive && isSolid && (
              <span className="absolute bottom-0 inset-x-0 h-[2.5px] bg-gradient-to-r from-sky-300 via-indigo-200 to-purple-300 shadow-[0_0_8px_rgba(165,180,252,0.9)]" />
            )}
          </Button>
        );
      })}
    </nav>
  );
}
